import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IItemForGetShortInfo, IGetCategoriesShortResponse, IGetCategoriesShort } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { getCategoriesShortInfo } from 'backend/categories';
import style from './CategoryMultipleSelect.module.scss';

interface ICategoryMultipleSelect {
	setIDsCategories: (value: string[]) => void;
	setFetchedCategories: (value: IItemForGetShortInfo[]) => void;
	updateCategories: (value: IItemForGetShortInfo[]) => void;
	changeLoaderStatus: (value: boolean) => void;
	IDsSelectedRestaurant: string[];
	initialIDsSelectedCategories: string[];
	IDsSelectedCategories: string[];
}

const CategoryMultipleSelect: React.FC<ICategoryMultipleSelect> = ({
	setIDsCategories,
	setFetchedCategories,
	updateCategories,
	changeLoaderStatus,
	IDsSelectedRestaurant,
	initialIDsSelectedCategories,
	IDsSelectedCategories,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<
		IGetCategoriesShortResponse,
		IGetCategoriesShort
	>(IDsSelectedRestaurant.length ? getCategoriesShortInfo : null, {
		restaurantIds: IDsSelectedRestaurant,
	});
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedCategories.filter((el) => el !== id);
			setIDsCategories(updatedSelectedItems);
		},
		[IDsSelectedCategories, setIDsCategories],
	);

	const categoriesList = useMemo(() => {
		return IDsSelectedCategories.map((el) => {
			const label = response?.data.categories.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [IDsSelectedCategories, response, handleRemove]);

	useEffect(() => {
		if (IDsSelectedRestaurant.length) {
			refetch();
		} else {
			setIDsCategories([]);
		}
	}, [IDsSelectedRestaurant.length]);

	useEffect(() => {
		if (response) {
			setFetchedCategories(response.data.categories);
			updateCategories(response.data.categories);
		}
		if (isFirstRender && response) {
			setIDsCategories(initialIDsSelectedCategories);
			setIsFirstRender(false);
			!initialIDsSelectedCategories.length && changeLoaderStatus(false);
		}
	}, [response, isFirstRender, initialIDsSelectedCategories]);

	return (
		<div className={style.container} data-test="CategoryMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.categories || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
					IDsSelectedCategories={IDsSelectedCategories}
					setIDsCategories={setIDsCategories}
				/>
			</div>
			<div className={style.groupsList}>{categoriesList}</div>
		</div>
	);
};

export default React.memo(CategoryMultipleSelect);
